import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import cn from 'classnames';
import { Url } from '@wix/da-url';
import { PapiSubscription } from '@wix/da-papi-types';
import CorePrice from '../../../CorePrice';
import { HERO_TABLE_ID } from '../../constants';
import { CoreTypes } from '../../../types';
import { type PackagePickerStoreSlice } from '../../../../../redux/packagePicker/types';
import { formatSaleEndDate } from '../../../utils';
import BoostExperienceSection from './_partials/BoostExperienceSection';
import DreamUpSection from './_partials/DreamUpSection';
import EarningsSection from './_partials/EarningsSection';
import ProfileAndSubmissionsSection from './_partials/ProfileAndSubmissionsSection';

import s from './HeroTableDesktop.scss';

interface Props {
  currentCoreSubscription?: PapiSubscription;
  referer: string;
  sale?: PackagePickerStoreSlice['sale'];
  saleEndDate?: string;
}

const HeroTableDesktop: React.FC<Props> = ({
  currentCoreSubscription,
  referer,
  sale,
  saleEndDate,
}) => {
  const { t } = useTranslation();
  const { ppReverseOrder } = sale || {};

  const highlightPackage = useMemo(() => {
    const hasCoreAccess =
      currentCoreSubscription?.corePackage?.coreType === 'core_access';
    const hasCorePlus =
      currentCoreSubscription?.corePackage?.coreType === 'core_plus';
    const hasCorePro =
      currentCoreSubscription?.corePackage?.coreType === 'core_pro';
    const hasCoreProPlus =
      currentCoreSubscription?.corePackage?.coreType === 'core_pro_plus';
    const recPackage = sale?.saleRecommendedPackage;

    if (!sale) {
      return (
        <div
          className={cn(
            s['table-frame'],
            hasCoreAccess && s['table-frame-access'],
            hasCorePlus && s['table-frame-plus'],
            hasCorePro && s['table-frame-pro'],
            hasCoreProPlus && s['table-frame-pro-plus']
          )}
        ></div>
      );
    } else if (recPackage) {
      return (
        <div
          className={cn(
            s['table-frame'],
            recPackage === 'core_access' && s['table-frame-access'],
            recPackage === 'core_plus' && s['table-frame-plus'],
            recPackage === 'core_pro' && s['table-frame-pro'],
            recPackage === 'core_pro_plus' && s['table-frame-pro-plus']
          )}
        ></div>
      );
    }
  }, [currentCoreSubscription, sale]);

  const refererIsDreamUp = referer.startsWith(Url.dreamupLink());

  return (
    <div
      className={cn(s['table-wrapper'], ppReverseOrder && s['reverse-order'])}
      id={HERO_TABLE_ID}
    >
      <div className={s['frame-wrapper']}>
        {highlightPackage}
        <table className={s['hero-table']}>
          <tbody>
            <tr>
              <th className={s['header-column']}></th>
              <th className={cn(s['core-price'], s['core-access'])}>
                <CorePrice coreType={CoreTypes.CORE_ACCESS} />
              </th>
              <th className={cn(s['core-price'], s['core-plus'])}>
                <CorePrice coreType={CoreTypes.CORE_PLUS} />
              </th>
              <th className={cn(s['core-price'], s['core-pro'])}>
                <CorePrice coreType={CoreTypes.CORE_PRO} />
              </th>
              <th className={cn(s['core-price'], s['core-pro-plus'])}>
                <CorePrice coreType={CoreTypes.CORE_PRO_PLUS} />
              </th>
            </tr>

            {!refererIsDreamUp ? (
              <BoostExperienceSection withTableWrapper={false} />
            ) : null}
          </tbody>
        </table>
      </div>

      {refererIsDreamUp && (
        <>
          <DreamUpSection highlightPackage={highlightPackage} />
          <BoostExperienceSection highlightPackage={highlightPackage} />
        </>
      )}

      <EarningsSection highlightPackage={highlightPackage} />
      <ProfileAndSubmissionsSection highlightPackage={highlightPackage} />

      {!refererIsDreamUp && (
        <DreamUpSection highlightPackage={highlightPackage} />
      )}

      <div className={s['frame-wrapper']}>
        {sale && sale.ppShowLegal && (
          <div className={s['disclaimer']}>
            {sale?.ppShowLegal && saleEndDate && (
              <div className={s['disclaimer']}>
                {t('packagePicker.heroTable.saleDisclaimer', {
                  endDate: formatSaleEndDate(saleEndDate),
                })}
              </div>
            )}
          </div>
        )}
        <div className={s['author']}>
          <a href={'https://www.deviantart.com/erisiar/art/Camping-843924883'}>
            {t('packagePicker.heroTable.authorCredits')}
          </a>
        </div>
      </div>
    </div>
  );
};
HeroTableDesktop.displayName = 'HeroTableDesktop';

export default HeroTableDesktop;
